import { useEffect, useState } from 'react'
import { magasin } from '../lib/client/store.ts'
import { useMagasin } from '../lib/client/hooks.ts'
import { signOut } from '../lib/auth-client.ts'
import { oublierSessionLocale } from '../lib/client/session-locale.ts'
import { Sheet } from '../components/Sheet.tsx'
import { AClasser } from '../components/AClasser.tsx'
import { Croix } from '../components/Icone.tsx'

type Personne = { id: string; name: string; emoji: string | null }
type Gout = {
  id: string
  personId: string | null
  kind: 'allergie' | 'regime' | 'n_aime_pas' | 'regle'
  severity: string
  freeText: string | null
}

const TYPES: Array<[Gout['kind'], string]> = [
  ['n_aime_pas', "N'aime pas"],
  ['allergie', 'Allergie'],
  ['regime', 'Régime'],
  ['regle', 'Règle du foyer'],
]

/**
 * Réglages et goûts.
 *
 * Le texte libre est volontairement mis en avant : c'est LUI qui fait
 * autorité pour l'assistant. « Léa déteste la texture des champignons cuits
 * mais mange les crus » est une information qu'aucune énumération ne
 * capturerait, et c'est exactement ce genre de nuance qui fait la différence
 * entre une suggestion de menu utile et une suggestion qu'on ignore.
 */
export function Reglages({ ouverte, onFermer }: { ouverte: boolean; onFermer: () => void }) {
  const { etat } = useMagasin()
  const [nouveau, setNouveau] = useState('')
  const [texte, setTexte] = useState('')
  const [pour, setPour] = useState<string | null>(null)
  const [type, setType] = useState<Gout['kind']>('n_aime_pas')
  const [theme, setTheme] = useState(() => localStorage.getItem('popote:theme') ?? 'systeme')
  const [invitation, setInvitation] = useState<{ code: string; url: string } | null>(null)
  const [copie, setCopie] = useState(false)

  useEffect(() => {
    if (!ouverte || invitation) return
    void fetch('/api/invitation')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setInvitation(d))
      .catch(() => {
        /* hors-ligne : la section reste masquée, elle n'a pas d'urgence */
      })
  }, [ouverte, invitation])

  const vivant = <T,>(m: Map<string, unknown>) =>
    [...m.values()].filter((l) => !(l as { deletedAt?: unknown }).deletedAt) as T[]

  const personnes = vivant<Personne>(etat.people)
  const gouts = vivant<Gout>(etat.preferences)

  function appliquerTheme(v: string) {
    setTheme(v)
    if (v === 'systeme') localStorage.removeItem('popote:theme')
    else localStorage.setItem('popote:theme', v)
    const sombre =
      v === 'sombre' ||
      (v === 'systeme' && matchMedia('(prefers-color-scheme: dark)').matches)
    document.documentElement.dataset.theme = sombre ? 'sombre' : 'clair'
  }

  return (
    <Sheet ouverte={ouverte} onFermer={onFermer} titre="Réglages">
      {/* En PREMIER, et seulement s'il y a quelque chose à faire : c'est la
          seule section des réglages qui soit une tâche en attente plutôt
          qu'une préférence. */}
      <AClasser />

      <section className="mb-6">
        <h3 className="mb-2 text-label uppercase text-muted">Thème</h3>
        <div className="flex gap-2">
          {(
            [
              ['systeme', 'Système'],
              ['clair', 'Clair'],
              ['sombre', 'Sombre'],
            ] as const
          ).map(([v, label]) => (
            <button
              key={v}
              type="button"
              onClick={() => appliquerTheme(v)}
              aria-pressed={theme === v}
              className={`pressable h-10 flex-1 rounded-sm text-meta ${
                theme === v ? 'bg-accent text-accent-ink' : 'bg-surface-sunk text-ink'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </section>

      {invitation && (
        <section className="mb-6">
          <h3 className="mb-2 text-label uppercase text-muted">Inviter quelqu'un</h3>
          <p className="mb-2 text-body text-muted">
            Transmets-lui l'adresse et ce code. Il créera son propre compte et verra le même
            inventaire.
          </p>
          <div className="rounded-sm bg-surface-sunk p-3">
            <p className="text-meta text-muted">Adresse</p>
            {/* select-all : c'est fait pour être copié, pas lu. */}
            <p className="mb-2 text-body text-ink select-all">{invitation.url}</p>
            <p className="text-meta text-muted">Code d'invitation</p>
            <p className="text-head text-ink select-all">{invitation.code}</p>
          </div>
          <button
            type="button"
            onClick={async () => {
              const texte = `Rejoins Popote : ${invitation.url}\nCode d'invitation : ${invitation.code}`
              try {
                /*
                 * `navigator.share` d'abord : sur iPhone c'est LE geste
                 * attendu — ça ouvre la feuille de partage et on envoie le
                 * tout par Messages en deux taps. Le presse-papier n'est que
                 * le repli pour un navigateur de bureau.
                 */
                if (navigator.share) await navigator.share({ text: texte })
                else {
                  await navigator.clipboard.writeText(texte)
                  setCopie(true)
                  setTimeout(() => setCopie(false), 2000)
                }
              } catch {
                /* partage annulé : rien à signaler, ce n'est pas une erreur */
              }
            }}
            className="pressable mt-2 h-11 w-full rounded-sm bg-accent text-body text-accent-ink"
          >
            {copie ? 'Copié' : "Partager l'invitation"}
          </button>
        </section>
      )}

      <section className="mb-6">
        <h3 className="mb-2 text-label uppercase text-muted">Qui mange ici</h3>
        <p className="mb-2 text-body text-muted">
          Pour que Claude tienne compte des goûts de chacun. Un enfant n'a pas besoin de compte
          pour figurer ici.
        </p>
        <ul className="mb-2 flex flex-wrap gap-1.5">
          {personnes.map((p) => (
            <span key={p.id} className="rounded-full bg-surface-sunk px-3 py-1.5 text-meta text-ink">
              {p.emoji} {p.name}
            </span>
          ))}
        </ul>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            const n = nouveau.trim()
            if (!n) return
            magasin.dispatch('person.upsert', { id: magasin.id(), name: n })
            setNouveau('')
          }}
        >
          <input
            value={nouveau}
            onChange={(e) => setNouveau(e.target.value)}
            placeholder="Ajouter quelqu'un sans compte…"
            enterKeyHint="done"
            className="h-[48px] w-full rounded-sm bg-surface-sunk px-3 text-field text-ink outline-none"
          />
        </form>
      </section>

      <section className="mb-6">
        <h3 className="mb-1 text-label uppercase text-muted">Goûts et contraintes</h3>
        <p className="mb-2 text-body text-muted">
          Tu peux aussi les dicter à Claude en conversation — il les enregistre ici, et il les
          relit avant chaque proposition de menu.
        </p>
        <ul className="mb-3">
          {gouts.map((g) => {
            const qui = g.personId
              ? (personnes.find((p) => p.id === g.personId)?.name ?? '?')
              : 'Tout le foyer'
            return (
              <li key={g.id} className="flex items-baseline justify-between gap-3 border-b border-line py-2">
                <span className="min-w-0 flex-1">
                  <span className="text-body text-ink">{g.freeText}</span>
                  <span className="ml-2 text-meta text-muted">
                    {qui} · {TYPES.find(([k]) => k === g.kind)?.[1]}
                  </span>
                </span>
                <button
                  type="button"
                  aria-label="Supprimer"
                  onClick={() => magasin.dispatch('pref.delete', { id: g.id })}
                  className="pressable shrink-0 text-meta text-muted"
                >
                  <Croix className="size-4" />
                </button>
              </li>
            )
          })}
          {gouts.length === 0 && (
            <li className="py-2 text-body text-muted">
              Rien pour l'instant. Écris-les en toutes lettres : c'est ce que Claude lit le mieux.
            </li>
          )}
        </ul>

        {/*
          Le formulaire se lisait mal : deux menus déroulants et un champ,
          sans bouton, et rien ne disait qu'il fallait valider au clavier.
          Les menus paraissaient même filtrer la liste au-dessus.

          Trois corrections : le CHAMP passe en premier (c'est lui qui porte
          le sens), les menus sont ÉTIQUETÉS et relégués en second, et un
          bouton explicite apparaît — désactivé tant qu'il n'y a rien à
          enregistrer.
        */}
        <form
          className="flex flex-col gap-2 rounded-md bg-surface-sunk p-3"
          onSubmit={(e) => {
            e.preventDefault()
            const t = texte.trim()
            if (!t) return
            magasin.dispatch('pref.upsert', {
              id: magasin.id(),
              personId: pour,
              kind: type,
              freeText: t,
            })
            setTexte('')
          }}
        >
          <label className="flex flex-col gap-1">
            <span className="text-meta text-muted">La préférence, en toutes lettres</span>
            <input
              value={texte}
              onChange={(e) => setTexte(e.target.value)}
              placeholder="ex. déteste les champignons cuits, mange les crus"
              enterKeyHint="done"
              autoCorrect="off"
              className="h-[48px] w-full rounded-sm bg-surface px-3 text-field text-ink outline-none"
            />
          </label>

          <div className="flex gap-2">
            <label className="flex min-w-0 flex-1 flex-col gap-1">
              <span className="text-meta text-muted">Pour qui</span>
              <select
                value={pour ?? ''}
                onChange={(e) => setPour(e.target.value || null)}
                className="h-[48px] w-full rounded-sm bg-surface px-2 text-field text-ink"
              >
                <option value="">Tout le foyer</option>
                {personnes.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex min-w-0 flex-1 flex-col gap-1">
              <span className="text-meta text-muted">Type</span>
              <select
                value={type}
                onChange={(e) => setType(e.target.value as Gout['kind'])}
                className="h-[48px] w-full rounded-sm bg-surface px-2 text-field text-ink"
              >
                {TYPES.map(([k, label]) => (
                  <option key={k} value={k}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <button
            type="submit"
            disabled={!texte.trim()}
            className="pressable h-11 w-full rounded-sm bg-accent text-body text-accent-ink disabled:opacity-40"
          >
            Enregistrer
          </button>

          <p className="text-meta text-muted">
            Une allergie bloque une recette ; un dégoût est seulement signalé.
          </p>
        </form>
      </section>

      <section className="mb-4">
        <h3 className="mb-2 text-label uppercase text-muted">Claude</h3>
        <p className="mb-2 text-body text-muted">
          Pour brancher Popote sur Claude, ajoute un connecteur personnalisé avec cette
          adresse&nbsp;:
        </p>
        {/* Sélectionnable : c'est une adresse qu'on doit pouvoir copier. */}
        <code className="block rounded-sm bg-surface-sunk px-3 py-2 text-meta text-ink select-all">
          {location.origin}/mcp
        </code>
      </section>

      <button
        type="button"
        onClick={() => {
          // On efface la mémoire locale AVANT l'appel réseau : se déconnecter
          // doit fonctionner même sans réseau, sinon on resterait connecté
          // dans l'interface après avoir demandé le contraire.
          oublierSessionLocale()
          void signOut().finally(() => location.reload())
        }}
        className="pressable mb-4 h-11 w-full rounded-sm bg-surface-sunk text-body text-muted"
      >
        Se déconnecter
      </button>
    </Sheet>
  )
}
